import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Globe, Calendar, CreditCard, UserCircle, MapPin, BarChart2, Star } from 'lucide-react';
import axios from 'axios';

import { PRIMARY_BG } from '../../theme/brand';
ChartJS.register(ArcElement, Tooltip);

const SEGMENT_COLORS = [
  '#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', PRIMARY_BG,
  '#84cc16', '#a855f7', '#94a3b8',
];

const KEY_META = {
  country:      { icon: Globe,       color: '#22c55e' },
  age:          { icon: Calendar,    color: '#3b82f6' },
  city:         { icon: MapPin,      color: '#f97316' },
  income_level: { icon: CreditCard,  color: '#06b6d4' },
  vip:          { icon: Star,        color: '#f59e0b' },
  gender:       { icon: UserCircle,  color: '#ec4899' },
};

const getApiHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});

const percentageLabelPlugin = {
  id: 'percentageLabels',
  afterDatasetDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      const total = dataset.data.reduce((a, b) => a + b, 0);
      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        const pct = Math.round((value / total) * 100);
        if (pct < 5) return;
        const pos = element.tooltipPosition();
        ctx.save();
        ctx.fillStyle = 'white';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${pct}%`, pos.x, pos.y);
        ctx.restore();
      });
    });
  },
};

function DemographicCard({ chartKey, title, subtitle, segments, chartTotal }) {
  const meta = KEY_META[chartKey] || { icon: BarChart2, color: '#8b5cf6' };
  const Icon = meta.icon;
  const accentColor = meta.color;

  const coloredSegments = segments.map((seg, i) => ({
    ...seg,
    color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
  }));

  const chartData = {
    labels: coloredSegments.map(s => s.label),
    datasets: [{
      data: coloredSegments.map(s => s.count),
      backgroundColor: coloredSegments.map(s => s.color),
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const seg = segments[ctx.dataIndex];
            return ` ${ctx.label}: ${seg.percentage}% (${ctx.parsed.toLocaleString()})`;
          },
        },
      },
    },
  };

  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accentColor}18`, color: accentColor }}>
          <Icon size={14} />
        </div>
        <div>
          <h3 className="text-sm font-semibold leading-tight" style={{ color: 'var(--t-text)' }}>{title}</h3>
          <p className="text-[11px]" style={{ color: 'var(--t-muted)' }}>{subtitle}</p>
        </div>
      </div>

      <div className="flex justify-center my-2" style={{ maxWidth: 180, margin: '0 auto' }}>
        <Pie data={chartData} options={chartOptions} plugins={[percentageLabelPlugin]} />
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center mt-4 mb-3">
        {coloredSegments.map(item => (
          <div key={item.label} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: item.color }} />
            <span className="text-[11px] font-medium" style={{ color: 'var(--t-text2)' }}>{item.label}</span>
          </div>
        ))}
      </div>

      <div className="pt-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--t-border)' }}>
        <span className="text-[11px]" style={{ color: 'var(--t-muted)' }}>Total Customers</span>
        <span className="text-sm font-bold" style={{ color: 'var(--t-text)' }}>{chartTotal.toLocaleString()}</span>
      </div>
    </div>
  );
}

DemographicCard.propTypes = {
  chartKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
  })).isRequired,
  chartTotal: PropTypes.number.isRequired,
};

function CardSkeleton() {
  return (
    <div className="rounded-xl p-5 animate-pulse" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg" style={{ background: 'var(--t-border)' }} />
        <div className="flex-1">
          <div className="h-3 rounded w-24 mb-1" style={{ background: 'var(--t-border)' }} />
          <div className="h-2 rounded w-32" style={{ background: 'var(--t-border)' }} />
        </div>
      </div>
      <div className="rounded-full mx-auto" style={{ width: 160, height: 160, background: 'var(--t-border)' }} />
      <div className="flex gap-3 justify-center mt-4 mb-3">
        {[1, 2, 3].map(i => <div key={i} className="h-2 rounded w-12" style={{ background: 'var(--t-border)' }} />)}
      </div>
      <div className="pt-3 flex justify-between" style={{ borderTop: '1px solid var(--t-border)' }}>
        <div className="h-3 rounded w-20" style={{ background: 'var(--t-border)' }} />
        <div className="h-3 rounded w-8" style={{ background: 'var(--t-border)' }} />
      </div>
    </div>
  );
}

export default function DemographicsTab() {
  const [loading, setLoading] = useState(false);
  const [charts, setCharts] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const fetchDemography = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/demography`;
      const response = await axios.get(url, { headers: getApiHeaders() });

      if (response.data.success) {
        setTotalCustomers(response.data.total_customers || 0);
        setCharts(response.data.charts || []);
      }
    } catch (error) {
      console.error('Error fetching demographics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemography();
  }, []);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--t-text)' }}>Customer Demographics</h1>
        <p className="text-sm" style={{ color: 'var(--t-muted)' }}>
          Breakdown of customer profile attributes and characteristics
          {totalCustomers > 0 && (
            <span className="ml-2 font-semibold" style={{ color: 'var(--t-text2)' }}>
              — {totalCustomers.toLocaleString()} total customers
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
          : charts.map(chart => (
              <DemographicCard
                key={chart.key}
                chartKey={chart.key}
                title={chart.title}
                subtitle={chart.subtitle}
                segments={chart.segments}
                chartTotal={chart.chart_total}
              />
            ))
        }
      </div>
    </div>
  );
}
